import {
  closeCurrentDialog,
  IconButton,
  openConfirmDialog,
  openDialog,
  Tooltip,
} from 'platform/components';
import {HStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {always, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  AdvertisementStatusEnum,
  CommonDescriptionAttribute,
  useGetGeneralSettingsQuery,
  useGetVehicleQuery,
  useRepeatLastActionMutation,
  useUpdateVehicleOnPlatformMutation,
} from '@dms/api/sales';
import {AdvertisingPlatformCodeEnum, EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  convertHtmlToFormat,
  countMobileDeMarkupOverhead,
  DEFAULT_PLATFORM_FORMAT,
  handleApiError,
  PLATFORM_FORMAT_MAP,
  usePermissions,
} from '@dms/shared';

import {useAdvertisementPolling} from '../contexts/AdvertisementPollingContext';
import {useGeneratedDescriptions} from '../contexts/GeneratedDescriptionsContext';
import {ConfirmGeneratedDescriptionsDialog} from './ConfirmGeneratedDescriptionsDialog';
import {PlatformEdit} from './PlatformEdit';

interface ActionsProps {
  vehicleId: string;
  platformCode: AdvertisingPlatformCodeEnum;
  currentStatus: AdvertisementStatusEnum;
  platformTitle: string;
  inSale: boolean;
  statusTriggeredAt: string | null;
}

export const PlatformActions = (props: ActionsProps) => {
  const [updateVehicleOnPlatform] = useUpdateVehicleOnPlatformMutation();
  const [repeatLastAction] = useRepeatLastActionMutation();
  const {resetPollingInterval} = useAdvertisementPolling();
  const {generatedPlatformDescriptions, formSubmitRef} = useGeneratedDescriptions();
  const [clickedAt, setClickedAt] = useState<string | null | undefined>(undefined);
  const {data: generalSettings} = useGetGeneralSettingsQuery({vehicleId: props.vehicleId});

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });

  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});

  const [
    hasEditPlatformSettingsPermission,
    hasRepeatLastPublishingActionPermission,
    hasUpdateVehiclePlatformPermission,
  ] = usePermissions({
    permissionKeys: ['editPlatformSettings', 'repeatLastPublishingAction', 'updateVehiclePlatform'],
    scopes: {
      editPlatformSettings: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      repeatLastPublishingAction: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      updateVehiclePlatform: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  // Prevent BE errors when interval between multiple triggers is less than approx. 5s
  const preventMultipleTriggers = clickedAt === props.statusTriggeredAt;

  const executeUpdate = () => {
    resetPollingInterval();
    setClickedAt(props.statusTriggeredAt);
    updateVehicleOnPlatform({vehicleId: props.vehicleId, platformCode: props.platformCode})
      .unwrap()
      .catch(handleApiError);
  };

  const handleUpdate = () => {
    const descriptionAttribute = generalSettings?.general?.attributes?.find(
      (attr): attr is CommonDescriptionAttribute => attr.code === 'description'
    );
    const descriptionHtml = descriptionAttribute?.value ?? null;

    if (descriptionHtml) {
      const config = PLATFORM_FORMAT_MAP[props.platformCode] ?? DEFAULT_PLATFORM_FORMAT;
      const {characterLimit} = config;

      if (characterLimit !== null) {
        const converted = convertHtmlToFormat(descriptionHtml, config.format);
        const isMobileDe = config.format === 'mobileDe';
        const markupOverhead = isMobileDe ? countMobileDeMarkupOverhead(converted) : 0;
        const count = isMobileDe ? converted.length - markupOverhead : converted.length;

        if (count > characterLimit) {
          openConfirmDialog({
            text: `${i18n.t('page.advertisement.labels.characterLimitExceeded')}\n\n${props.platformTitle}: ${count} / ${characterLimit} (${i18n.t('page.advertisement.labels.exceededBy')} ${count - characterLimit})`,
            onConfirm: executeUpdate,
          });
          return;
        }
      }
    }

    executeUpdate();
  };

  const handleRepeatLastAction = () => {
    resetPollingInterval();
    setClickedAt(props.statusTriggeredAt);
    repeatLastAction({vehicleId: props.vehicleId, platformCode: props.platformCode})
      .unwrap()
      .catch(handleApiError);
  };

  const openPlatformDetail = () => {
    openDialog(
      <PlatformEdit
        vehicleId={props.vehicleId}
        platformCode={props.platformCode}
        platformName={props.platformTitle}
        isDisabled={!hasEditPlatformSettingsPermission}
      />,
      {
        title: i18n.t('entity.vehicle.labels.listingAttributes') + ` "${props.platformTitle}"`,
      }
    );
  };

  const handleOpenDetail = () => {
    const hasSuccessfulGenerated =
      (generatedPlatformDescriptions?.filter((platform) => isNil(platform.error)).length ?? 0) > 0;
    // The form must be mounted to save it; otherwise there is nothing to confirm here.
    const canConfirmDescriptions = hasSuccessfulGenerated && isNotNil(formSubmitRef.current);

    if (!canConfirmDescriptions) {
      openPlatformDetail();
      return;
    }

    openDialog(
      <ConfirmGeneratedDescriptionsDialog
        onConfirm={async () => {
          const didSave = await formSubmitRef.current?.();
          closeCurrentDialog();
          // Stay on the list when FE validation fails so the user can fix the form.
          if (didSave === true) {
            openPlatformDetail();
          }
        }}
        onDecline={closeCurrentDialog}
        data-testid={`${props.platformCode}-confirm-generated-descriptions`}
      />,
      {
        title: i18n.t('page.advertisement.labels.confirmGeneratedDescriptionsTitle'),
        'data-testid': `${props.platformCode}-confirm-generated-descriptions-dialog`,
      }
    );
  };

  const showUpdateAction = props.currentStatus === AdvertisementStatusEnum.PUBLISHED;

  const showRepeatAction = match(props.currentStatus)
    .with(
      AdvertisementStatusEnum.PUBLISH_FAILED,
      AdvertisementStatusEnum.UNPUBLISH_FAILED,
      AdvertisementStatusEnum.UPDATE_FAILED,
      always(true)
    )
    .otherwise(always(false));

  return (
    <HStack align="center" justify="flex-end">
      <Show when={hasRepeatLastPublishingActionPermission && showRepeatAction && props.inSale}>
        <Tooltip label={i18n.t('page.advertisement.actions.repeatLastAction')}>
          <IconButton
            icon="AV/replay"
            onClick={handleRepeatLastAction}
            data-testid={`${props.platformCode}-repeat-last-action`}
            isDisabled={preventMultipleTriggers}
          />
        </Tooltip>
      </Show>

      <Show when={hasUpdateVehiclePlatformPermission && showUpdateAction && props.inSale}>
        <Tooltip label={i18n.t('page.advertisement.actions.updateAd')}>
          <IconButton
            icon="action/update"
            onClick={handleUpdate}
            data-testid={`${props.platformCode}-update-ad`}
            isDisabled={preventMultipleTriggers}
          />
        </Tooltip>
      </Show>

      <Tooltip
        label={`${i18n.t('page.advertisement.actions.editPlatform')} ${props.platformTitle}`}
      >
        <IconButton
          icon="navigation/chevron_right"
          onClick={handleOpenDetail}
          data-testid={`link-advertisement-platforms.${props.platformCode}`}
        />
      </Tooltip>
    </HStack>
  );
};
